import type {
    ProductLot,
    ProductLotStockInNoteItem,
} from '@/lib/api/product-lot-api'
import type { ProductLotOption } from './ProductLotItemsField'
import {
    LotOptionRow,
    createdAtLine,
    createdAtText,
} from '@/components/lots/lot-option-row'
import { lotDiscountLabel } from '@/components/products/stock-tier-label'
import { formatQty } from '@/lib/format'

// NSX/HSD hiển thị DD/MM/YYYY.
function fmtLotDate(iso?: string | null): string {
    if (!iso) return ''
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ''
    const dd = String(d.getDate()).padStart(2, '0')
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    return `${dd}/${mm}/${d.getFullYear()}`
}

export function datesLine(
    nsx?: string | null,
    hsd?: string | null,
): string | undefined {
    const parts: string[] = []
    if (nsx) parts.push(`NSX ${fmtLotDate(nsx)}`)
    if (hsd) parts.push(`HSD ${fmtLotDate(hsd)}`)
    return parts.length ? parts.join(' · ') : undefined
}

/** Chữ trên NÚT chọn lô ở kiểu sổ: "tên — ngày giờ tạo". Lô có CK thì CK đứng
 *  ĐẦU (M-129): ô chọn của sổ hẹp, tên + ngày giờ đã dài hơn ô nên đặt cuối là
 *  bị cắt mất đúng thứ người ghi cần để phân biệt hai lô cùng loại túi. */
export function ledgerLotTriggerLabel(
    productName: string | undefined,
    createdAt: string | null | undefined,
    discountPercent: number | undefined,
): string {
    const base = `${productName ?? ''} — ${createdAtText(createdAt) ?? ''}`
    const ck = lotDiscountLabel(discountPercent)
    return ck ? `${ck} — ${base}` : base
}

/** Option cho lô có sẵn (fetch từ danh sách active) — render đầy đủ: ảnh + 3 dòng.
 *
 *  `ledger=true` là kiểu dành cho các cuốn sổ (#M-16): giấu mã lô và NSX/HSD,
 *  phóng to ngày giờ tạo. Nhãn tìm kiếm vẫn giữ mã lô để gõ mã vẫn ra. */
export function productLotToOption(
    l: ProductLot,
    ledger = false,
): ProductLotOption {
    const unit = l.product_unit ?? ''
    const qty = `${formatQty(l.quantity_available)}${unit ? ` ${unit}` : ''}`
    return {
        value: l.id,
        // `label` GIỮ mã lô kể cả ở kiểu sổ: nó là từ khoá gõ để tìm. Chữ
        // hiện trên nút sau khi chọn thì lấy `triggerLabel` — không có mã.
        label: `${l.code} — ${l.product_name ?? ''}`,
        triggerLabel: ledger
            ? ledgerLotTriggerLabel(
                  l.product_name,
                  l.created_at,
                  l.discount_percent,
              )
            : undefined,
        available: l.quantity_available,
        unit: l.product_unit,
        productId: l.product_id,
        discountPercent: l.discount_percent,
        render: () => (
            <LotOptionRow
                title={l.product_name ?? l.code}
                code={l.code}
                chips={[l.position_code]}
                qty={qty}
                thirdLine={datesLine(l.manufactured_date, l.expiry_date)}
                createdLine={
                    ledger
                        ? createdAtText(l.created_at)
                        : createdAtLine(l.created_at)
                }
                ledgerStyle={ledger}
                // Chỉ kiểu sổ (M-129): màn phiếu nhập/xuất kho giữ nguyên.
                discountLabel={
                    ledger ? lotDiscountLabel(l.discount_percent) : undefined
                }
                imageSlot
                imageUrl={l.product_image_url}
            />
        ),
    }
}

/** Option seed (lô đã có trong phiếu khi sửa) — render đầy đủ như fetched (ảnh +
 *  3 dòng); riêng số lượng là KL trong phiếu (lô pending chưa có tồn khả dụng). */
export function productNoteItemToOption(
    it: ProductLotStockInNoteItem,
): ProductLotOption {
    const code = it.product_lot_code ?? it.product_lot_id
    const unit = it.product_unit ?? ''
    return {
        value: it.product_lot_id,
        label: `${code} — ${it.product_name ?? ''}`,
        render: () => (
            <LotOptionRow
                title={it.product_name ?? code}
                code={code}
                chips={[it.position_code]}
                qty={`${formatQty(it.quantity)}${unit ? ` ${unit}` : ''}`}
                thirdLine={datesLine(it.manufactured_date, it.expiry_date)}
                imageSlot
                imageUrl={it.product_image_url}
            />
        ),
    }
}
